import { APP_DOMAIN } from 'services/constants';

/** Local webpack dev on dev.ommnio.localhost:3000 (no DNS/TLS setup). */
export const isLocalWebDev = () =>
  process.env.NODE_ENV === 'development' && APP_DOMAIN === 'dev.ommnio.localhost';

export const getDevWebPortSuffix = () => (isLocalWebDev() ? ':3000' : '');

export const getDevWebProtocol = () => (isLocalWebDev() ? 'http:' : 'https:');

/** `{subdomain?}{APP_DOMAIN}{devPort}` — canonical web app origin for links and redirects. */
export const buildWebOrigin = (subdomain = '') => {
  const sub = subdomain ? `${subdomain}.` : '';
  return `${getDevWebProtocol()}//${sub}${APP_DOMAIN}${getDevWebPortSuffix()}`;
};

/** Absolute URL on the web app host. */
export const buildWebUrl = (pathname, subdomain = '') => {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${buildWebOrigin(subdomain)}${path}`;
};

export const isValidCompanySubdomain = value =>
  typeof value === 'string' && /^[a-z0-9-]+$/i.test(value);

/** postMessage targetOrigin — prefer live page origin when hostname matches. */
export const getOAuthTargetOrigin = (subdomain = '') => {
  const expectedHostname = subdomain ? `${subdomain}.${APP_DOMAIN}` : APP_DOMAIN;
  if (typeof window !== 'undefined' && window.location.hostname === expectedHostname) {
    return window.location.origin;
  }
  return buildWebOrigin(subdomain);
};

/**
 * OAuth popup callbacks land on the apex host; the opener is on `{company}.{APP_DOMAIN}`.
 * Prefer the hostname subdomain when present, else the `subDomain` query param from the
 * backend redirect so postMessage targets the company opener.
 */
export const resolveOAuthCallbackTargetOrigin = (hostnameSubdomain, querySubdomain) => {
  const subdomain = [hostnameSubdomain, querySubdomain].find(isValidCompanySubdomain) || '';
  return getOAuthTargetOrigin(subdomain);
};

/** Whether the current page is the company-less apex host (dev / web / staging apex). */
export const isApexWebHostname = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => hostname === APP_DOMAIN;

/** Hostname is the web app apex or a company subdomain under APP_DOMAIN. */
export const isOmmnioWebHostname = hostname =>
  typeof hostname === 'string' &&
  Boolean(APP_DOMAIN) &&
  (hostname === APP_DOMAIN || hostname.endsWith(`.${APP_DOMAIN}`));

/** True when `origin` is an Ommnio web app origin for the current APP_DOMAIN. */
export const isTrustedWebOrigin = origin => {
  try {
    return isOmmnioWebHostname(new URL(origin).hostname);
  } catch {
    return false;
  }
};

/**
 * postMessage targetOrigin for iframe → parent (welcome-bot, OmmnioSign embeds).
 * Prefer document.referrer when it is an Ommnio web host; else same-origin.
 */
export const getEmbedParentTargetOrigin = () => {
  if (typeof window === 'undefined') {
    return buildWebOrigin();
  }
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer);
      if (isOmmnioWebHostname(ref.hostname)) {
        return ref.origin;
      }
    }
  } catch {
    // ignore invalid referrer
  }
  return window.location.origin;
};
